import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { SerializedSmartLink } from 'fym-common/src/db/serializersTypes';
import { PresaveCampaignStatus } from 'fym-shared/src/smartlinks/presave';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { isSmartLinkDraft } from '../utils/isSmartLinkDraft';
import { usePresaveCampaign } from './usePresaveCampaign';
import { PresaveStatusPill } from './PresaveStatusPill';
import { PresaveCountdownRow } from './PresaveCountdownRow';
import { PresaveTimeline } from './PresaveTimeline';
import { PresaveSavesCard } from './PresaveSavesCard';
import { PresaveEmailsCard } from './PresaveEmailsCard';
import { PresaveSubscribersCard } from './PresaveSubscribersCard';
import { PresaveChecklist } from './PresaveChecklist';
import { PresaveReleasedSummary } from './PresaveReleasedSummary';
import { PresavePanelFooter } from './PresavePanelFooter';
import { MarkReleasedModal } from './MarkReleasedModal';
import { CancelCampaignModal } from './CancelCampaignModal';
import { formatShortDay } from './presaveDates';
import { Panel, PanelBody, PanelHead } from '../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole panel; each part uses its own slice of it */
import styles from './PresavePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PresaveCampaignPanelProps {
  accountId: number;
  smartLink: SerializedSmartLink;
}

/**
 * The campaign behind a pre-save page, read as a campaign: how long is left,
 * where in the window we are, who has signed up and on what, and what is still
 * wrong with the page. It sits on the manage screen rather than in the Creator,
 * because a campaign is not a block — the Creator edits blocks, the block only
 * draws the campaign.
 */
const PresaveCampaignPanelComponent: React.FC<PresaveCampaignPanelProps> = ({ accountId, smartLink }) => {
  const intl = useIntl();
  const [isModalOpen, setModalOpen] = useState(false);
  const [isCancelModalOpen, setCancelModalOpen] = useState(false);
  const { hasPresaveBlock, services, campaignUuid, campaign, stats, isLoading, error, refetch } = usePresaveCampaign({
    accountId,
    smartLink,
  });

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);
  const openCancelModal = useCallback(() => setCancelModalOpen(true), []);
  const closeCancelModal = useCallback(() => setCancelModalOpen(false), []);
  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  if (!hasPresaveBlock) {
    return null;
  }

  if (error) {
    return (
      <Panel>
        <ErrorView error={error} retry={retry} />
      </Panel>
    );
  }

  if (isLoading || !campaign || !stats) {
    return (
      <Panel>
        <LoadingView />
      </Panel>
    );
  }

  const isScheduled = campaign.status === PresaveCampaignStatus.scheduled;
  const isReleased = campaign.status === PresaveCampaignStatus.released;

  return (
    <Panel className={styles.panel}>
      <PanelHead
        className={clsx(styles.head, isReleased && styles.headReleased)}
        lead={<PresaveStatusPill status={campaign.status} releaseDate={campaign.releaseDate} />}
        title={
          campaign.trackTitle.trim() === ''
            ? intl.formatMessage({ id: 'app.sml.presave.panel.label', defaultMessage: 'Pre-save campaign' })
            : intl.formatMessage(
                // Name the song: a Bio Link has several (QA 2026-10-28, brk-r2-5).
                { id: 'app.sml.presave.panel.label_titled', defaultMessage: 'Pre-save campaign · {title}' },
                { title: campaign.trackTitle.trim() }
              )
        }
        right={intl.formatMessage(
          { id: 'app.sml.presave.panel.opened', defaultMessage: 'opened {date}' },
          { date: formatShortDay(intl, campaign.createdAt) }
        )}
      />

      <PanelBody className={styles.body}>
        {isScheduled && (
          <>
            <PresaveCountdownRow releaseDate={campaign.releaseDate} isDraft={isSmartLinkDraft(smartLink)} />
            <PresaveTimeline startedAt={campaign.createdAt} releaseDate={campaign.releaseDate} />
          </>
        )}

        {isReleased ? (
          <PresaveReleasedSummary stats={stats} releaseDate={campaign.releaseDate} />
        ) : (
          <div className={styles.counters}>
            <PresaveSavesCard stats={stats} services={services} />
            <PresaveEmailsCard stats={stats} />
            {isScheduled && (
              <PresaveSubscribersCard count={stats.artistSubscriberCount ?? 0} title={campaign.trackTitle} />
            )}
          </div>
        )}

        {isScheduled && <PresaveChecklist smartLink={smartLink} />}
      </PanelBody>

      <PresavePanelFooter
        status={campaign.status}
        releaseDate={campaign.releaseDate}
        shareUrl={smartLink.shareUrl}
        songTitle={campaign.trackTitle}
        openReleaseModal={openModal}
        openCancelModal={openCancelModal}
      />

      <MarkReleasedModal
        isOpen={isModalOpen}
        closeModal={closeModal}
        accountId={accountId}
        campaignId={campaign.id}
        campaignUuid={campaignUuid}
        smartLinkId={smartLink.id}
      />

      <CancelCampaignModal
        isOpen={isCancelModalOpen}
        closeModal={closeCancelModal}
        accountId={accountId}
        campaignId={campaign.id}
        campaignUuid={campaignUuid}
        smartLinkId={smartLink.id}
      />
    </Panel>
  );
};

export const PresaveCampaignPanel = TypedMemo(PresaveCampaignPanelComponent);
